import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, MapPin, Globe, FileText, Video as VideoIcon } from "lucide-react";
import { api, mediaUrl, formatQuarter } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { FeaturedGrid } from "@/components/site/FeaturedSlider";
import { ImageQuad } from "@/components/site/ImageQuad";
import { EmptyState } from "@/components/site/EmptyState";
import { btnPrimary, btnSecondary } from "@/components/site/buttons";

const IVORY = "#F6F1E8";
const GOLD = "#B08A3C";
const STEEL = "#3F5968";
const CHARCOAL = "#33383D";

const ABOUT_FALLBACK =
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxlc3RhYmxpc2hlZCUyMGJ1c2luZXNzJTIwbWFuJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzkwMzczNTEwfDA&ixlib=rb-4.1.0&q=85";
const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBidXNpbmVzcyUyMHN0cmF0ZWd5JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDM3MzUxMHww&ixlib=rb-4.1.0&q=85";

const Overline = ({ children }) => (
  <p className="font-sans text-[13px] font-semibold uppercase" style={{ color: GOLD, letterSpacing: "0.14em" }}>{children}</p>
);
const Heading = ({ children, className = "" }) => (
  <h2 className={`font-serif font-light ${className}`} style={{ color: STEEL, fontSize: "clamp(30px, 4.6vw, 46px)", lineHeight: 1.1 }}>{children}</h2>
);
const Body = ({ children, className = "" }) => (
  <p className={`font-sans ${className}`} style={{ color: CHARCOAL, fontSize: "clamp(16px,1.4vw,18px)", lineHeight: 1.6 }}>{children}</p>
);
const ArchiveLink = ({ to, children }) => (
  <Link to={to} className="group inline-flex items-center gap-2 font-sans text-sm font-semibold tracking-wide" style={{ color: STEEL }}>
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: GOLD }} />
  </Link>
);

const Panel = ({ children, z, id }) => (
  <section id={id} className="stack-panel" style={{ zIndex: z }} aria-label={id}>
    <div className="mx-auto w-full max-w-7xl px-5 py-16 sm:py-20 lg:px-10 lg:py-24">{children}</div>
  </section>
);

// Compact horizontal list item for latest articles
const buildArticleSlides = (fa, latest) =>
  [...(fa ? [fa] : []), ...latest].filter(Boolean).map((a) => ({
    id: a.id, type: "article", href: `/articles/${a.slug}`, image: a.featured_image,
    alt: a.image_alt, category: a.category, title: a.title, description: a.excerpt,
    meta: `${a.author} · ${formatQuarter(a.published_at)} · ${a.reading_time} min read`,
  }));
const buildVideoSlides = (fv, latest) =>
  [...(fv ? [fv] : []), ...latest].filter(Boolean).map((v) => ({
    id: v.id, type: "video", href: `/videos/${v.slug}`, image: v.thumbnail,
    category: v.topic, title: v.title, description: v.description, duration: v.duration,
    meta: formatQuarter(v.published_at),
  }));

export default function Home() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/public/home").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="py-40 text-center font-sans" style={{ color: STEEL, backgroundColor: IVORY }}>Loading…</div>;

  const s = data.settings || {};
  const home = s.home || {};
  const fa = data.featured_article;
  const fv = data.featured_video;
  const latestA = data.latest_articles || [];
  const latestV = data.latest_videos || [];
  const articleSlides = buildArticleSlides(fa, latestA).slice(0, 3);
  const videoSlides = buildVideoSlides(fv, latestV).slice(0, 6);

  const heroPortrait = home.hero_image || (home.quad && home.quad[0] && home.quad[0].image);
  const heroImg = mediaUrl(heroPortrait) || HERO_FALLBACK;
  const heroAlt = home.hero_alt || (home.quad && home.quad[0] && home.quad[0].alt) || "George Goldman, principal of Goldman Fischer";

  // "About the Principal" media: show the 2x2 quad only once all four panels have images;
  // until then keep the existing single portrait visible to the public.
  const aboutQuadPanels = home.quad || [];
  const aboutQuadComplete = aboutQuadPanels.length >= 4 && aboutQuadPanels.slice(0, 4).every((p) => p && p.image);

  return (
    <>
      <Seo
        jsonLd={{
          "@context": "https://schema.org", "@type": "Organization", name: "Goldman Fischer",
          url: "https://www.goldmanfischer.com", telephone: "+16318040212",
          address: { "@type": "PostalAddress", streetAddress: "PO Box 319", addressLocality: "Centerport", addressRegion: "NY", postalCode: "11721" },
        }}
      />

      <div className="stack-scroll">
        {/* 1 — Introduction */}
        <Panel z={1} id="introduction">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Overline>Strategic Planning · Consultants &amp; Advisors</Overline>
              <h1 className="mt-6 font-serif font-light" style={{ color: STEEL, fontSize: "clamp(38px, 6vw, 64px)", lineHeight: 1.05, maxWidth: "16ch" }}>
                {home.intro_heading || "Clear thinking for important business decisions."}
              </h1>
              <Body className="mt-7 max-w-xl">{home.intro_body}</Body>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/articles" className={btnPrimary} data-testid="hero-explore-articles">Explore Articles <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/videos" className={btnSecondary} data-testid="hero-watch-videos">Watch Videos</Link>
              </div>
            </div>
            <div className="lg:pl-4">
              <div className="w-full overflow-hidden" style={{ border: `1px solid ${GOLD}55` }}>
                <img src={heroImg} alt={heroAlt} className="aspect-[16/9] w-full object-cover object-center" data-testid="hero-image" />
              </div>
            </div>
          </div>
        </Panel>

        {/* 2 — Featured Articles (horizontal sliding-window slider) */}
        <Panel z={2} id="featured-articles">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div><Overline>Featured Articles</Overline><Heading className="mt-3">Recent thinking</Heading></div>
            <ArchiveLink to="/articles">View More</ArchiveLink>
          </div>
          {articleSlides.length > 0 ? (
            <FeaturedGrid slides={articleSlides} />
          ) : (
            <EmptyState icon={FileText} title="Articles are on the way" body="Insights on strategic planning, management and governance will appear here once published." />
          )}
        </Panel>

        {/* 3 — Featured Videos (horizontal sliding-window slider) */}
        <Panel z={3} id="featured-videos">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div><Overline>Featured Videos</Overline><Heading className="mt-3">From the library</Heading></div>
            <ArchiveLink to="/videos">View all videos</ArchiveLink>
          </div>
          {videoSlides.length > 0 ? (
            <FeaturedGrid slides={videoSlides} />
          ) : (
            <EmptyState icon={VideoIcon} title="Video library coming soon" body="Short, practical videos will be published here. No video plays until you choose to load it." />
          )}
        </Panel>

        {/* 4 — Services */}
        {s.services && (
          <Panel z={4} id="services">
            <div className="max-w-2xl"><Overline>How we help</Overline><Heading className="mt-3">Strategic planning &amp; consulting services</Heading><Body className="mt-4">{s.services.intro}</Body></div>
            <div className="mt-12 grid gap-px overflow-hidden sm:grid-cols-2 lg:grid-cols-3" style={{ backgroundColor: `${GOLD}30` }}>
              {s.services.items.map((it, i) => (
                <div key={i} className="p-8" style={{ backgroundColor: IVORY }}>
                  <span className="font-serif text-3xl" style={{ color: GOLD }}>0{i + 1}</span>
                  <h3 className="mt-3 font-serif text-2xl font-light" style={{ color: STEEL }}>{it.title}</h3>
                  <Body className="mt-2 text-[16px]">{it.body}</Body>
                </div>
              ))}
            </div>
            <Link to="/services" className={`mt-10 inline-flex ${btnSecondary}`}>Explore our services</Link>
          </Panel>
        )}

        {/* 5 — About George Goldman */}
        {s.about && (
          <Panel z={5} id="about">
            <div className="grid items-center gap-12 lg:grid-cols-5">
              <div className="lg:col-span-2">
                {aboutQuadComplete ? (
                  <ImageQuad panels={aboutQuadPanels} />
                ) : (
                  <div className="aspect-[4/5] w-full overflow-hidden" style={{ border: `1px solid ${GOLD}55` }}>
                    <img src={mediaUrl(s.about.photo) || ABOUT_FALLBACK} alt={s.about.name} className="h-full w-full object-cover" />
                  </div>
                )}
              </div>
              <div className="lg:col-span-3">
                <Overline>About Goldman Fischer</Overline>
                <Heading className="mt-4">{s.about.name}</Heading>
                <div className="mt-5 h-px w-16" style={{ backgroundColor: GOLD }} />
                <Body className="mt-6 whitespace-pre-line">{s.about.bio}</Body>
                <div className="mt-8"><ArchiveLink to="/about">Read more about Goldman Fischer</ArchiveLink></div>
              </div>
            </div>
          </Panel>
        )}

        {/* 6 — Consultation & Contact */}
        <Panel z={6} id="contact">
          <div className="mx-auto max-w-3xl text-center">
            <Overline>Consultation &amp; Contact</Overline>
            <Heading className="mt-4">{home.consult_heading || "Ready to think it through together?"}</Heading>
            <Body className="mx-auto mt-5 max-w-xl">{home.consult_body || "Request a consultation to discuss your situation in confidence."}</Body>
            <div className="mt-9 flex justify-center">
              <Link to="/contact" className={btnPrimary} data-testid="cta-consult-button">Request a Consultation <ArrowRight className="h-4 w-4" /></Link>
            </div>
            {s.company && (
              <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 font-sans text-sm" style={{ color: STEEL }}>
                <a href={`tel:${s.company.phone_link}`} className="flex items-center gap-2 hover:opacity-80" data-testid="home-phone"><Phone className="h-4 w-4" style={{ color: GOLD }} /> {s.company.phone}</a>
                <span className="flex items-center gap-2"><MapPin className="h-4 w-4" style={{ color: GOLD }} /> {s.company.address}</span>
                <span className="flex items-center gap-2"><Globe className="h-4 w-4" style={{ color: GOLD }} /> {s.company.website}</span>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}
